import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import InventoryIcon from "@mui/icons-material/Inventory";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ScaleIcon from "@mui/icons-material/Scale";
import CategoryIcon from "@mui/icons-material/Category";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import * as services from "./../../services/saleng";
import { useState, useEffect } from "react";
import { formatNumber } from "../../utils/formatNumber";
import moment from "moment";
import "moment/locale/th";
import { hasPermission } from "../../auth/RequirePermission3";
import { alpha, styled } from "@mui/material/styles";

moment.locale("th");

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[50],
    "&:hover, &.Mui-hovered": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      "&:hover, &.Mui-hovered": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity
        ),
        // Reset on touch devices, it doesn't add specificity
        "@media (hover: none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
  border: "none",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f3f4f6",
    color: "#374151",
    fontSize: "0.95rem",
    fontWeight: "600",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #f3f4f6",
    whiteSpace: "nowrap", // Force single line
    display: "flex",
    alignItems: "center",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #f3f4f6",
  },
}));

export default function TransformTransactionsTemplate() {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const res = await services.getInventory();
        const inventoryRes = res.data.data;
        setInventory(inventoryRes || []);
      } catch (err) {
        setError(err.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // แปลง inventory เป็น rows สำหรับ DataGrid
  const rows = inventory.map((t, index) => ({
    id: index,
    code: t.product.code,
    unit: "กก.",
    name: t.product.title,
    // name: t.product.title, // Duplicate key removed
    totalValue: t.totalValue,
    category: t.product.category.name,
    quantity: t.quantity || 0,
    price: t.averageCost || 0,
    total: (t.quantity || 0) * (t.averageCost || 0),
    updatedAt: moment(t.updatedAt)
      .add(543, "year")
      .format("D MMM YYYY เวลา HH:mm"),
  }));

  // Calculate Summary Stats
  const totalItems = rows.length;
  const totalWeight = rows.reduce((sum, row) => sum + row.quantity, 0);
  const totalValue = rows.reduce((sum, row) => sum + row.total, 0);

  // กำหนด columns สำหรับ DataGrid
  const allColumns = [
    { 
      field: "code", 
      headerName: "รหัสสินค้า", 
      flex: 0.8, 
      minWidth: 90,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "name",
      headerName: "ชื่อสินค้า",
      flex: 2,
      minWidth: 200,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="500" noWrap>
          {params.value}
        </Typography>
      ),
    },
    { 
      field: "category", 
      headerName: "หมวดหมู่", 
      flex: 1, 
      minWidth: 110,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "quantity",
      headerName: "คงเหลือ (กก.)",
      type: "number",
      flex: 1,
      minWidth: 110,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight="bold"
          color={params.value > 0 ? "success.main" : "error.main"}
          noWrap
        >
          {formatNumber(params.value)}
        </Typography>
      ),
    },
    {
      field: "price",
      headerName: "ต้นทุน/หน่วย",
      type: "number",
      flex: 1,
      minWidth: 110,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params) =>
        params != null ? `${formatNumber(params)} บาท` : "-",
      permission: "inventory_view_changes",
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value != null ? `${formatNumber(params.value)} บาท` : "-"}
        </Typography>
      ),
    },
    {
      field: "totalValue",
      headerName: "มูลค่ารวม",
      type: "number",
      flex: 1.2,
      minWidth: 130,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params) =>
        params != null ? `${formatNumber(params)} บาท` : "-",
      permission: "inventory_view_changes",
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" color="primary.main" noWrap>
          {formatNumber(params.value)} บาท
        </Typography>
      ),
    },
    { 
      field: "updatedAt", 
      headerName: "อัปเดตล่าสุด", 
      flex: 1.2, 
      minWidth: 150,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      ),
    },
  ];

  const columns = allColumns.filter(
    (col) => !col.permission || hasPermission([col.permission])
  );

  const filteredRows = rows.filter(
    (row) =>
      row.name.toLowerCase().includes(search.toLowerCase()) ||
      row.updatedAt.toLowerCase().includes(search.toLowerCase()) ||
      row.code.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}>Loading inventory...</Box>;
  if (error) return <Box sx={{ p: 4, color: "red", textAlign: 'center' }}>Error: {error}</Box>;

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      <Box className="flex-none p-4 md:p-6 pb-0">
        <AppBreadcrumbs />
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mt-4 mb-6">
          <div>
            <Typography variant="h4" className="text-gray-800 font-bold mb-1">
              คลังสินค้า
            </Typography>
            <Typography variant="body2" className="text-gray-500">
              จัดการและตรวจสอบสินค้าคงคลังของคุณ
            </Typography>
          </div>
          
          <TextField
              size="small"
              placeholder="ค้นหาสินค้า..."
              value={search}
              onChange={(e) => setSearch(e.target.value.trimStart())}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className="text-gray-400" />
                  </InputAdornment>
                ),
                className: "bg-white rounded-xl",
              }}
              sx={{ 
                width: { xs: '100%', md: 300 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "white",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                  "& fieldset": { border: "1px solid #e5e7eb" },
                  "&:hover fieldset": { borderColor: "#d1d5db" },
                  "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
                }
              }}
            />
        </div>

        {/* Summary Cards */}
        <Grid container spacing={3} className="mb-6">
          <Grid item xs={12} sm={4}>
            <Card className="rounded-2xl shadow-sm border border-gray-100 h-full">
              <CardContent className="flex items-center p-4">
                <Box className="p-3 rounded-xl bg-blue-50 text-blue-600 mr-4">
                  <InventoryIcon fontSize="medium" />
                </Box>
                <div>
                  <Typography variant="body2" className="text-gray-500 mb-1">
                    รายการสินค้าทั้งหมด
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" className="text-gray-800">
                    {formatNumber(totalItems)} รายการ
                  </Typography>
                </div>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card className="rounded-2xl shadow-sm border border-gray-100 h-full">
              <CardContent className="flex items-center p-4">
                <Box className="p-3 rounded-xl bg-green-50 text-green-600 mr-4">
                  <ScaleIcon fontSize="medium" />
                </Box>
                <div>
                  <Typography variant="body2" className="text-gray-500 mb-1">
                    น้ำหนักรวมทั้งหมด
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" className="text-gray-800">
                    {formatNumber(totalWeight)} กก.
                  </Typography>
                </div>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            {hasPermission(["inventory_view_changes"]) && (
            <Card className="rounded-2xl shadow-sm border border-gray-100 h-full">
              <CardContent className="flex items-center p-4">
                <Box className="p-3 rounded-xl bg-amber-50 text-amber-600 mr-4">
                  <AttachMoneyIcon fontSize="medium" />
                </Box>
                <div>
                  <Typography variant="body2" className="text-gray-500 mb-1">
                    มูลค่าสินค้าคงเหลือ
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" className="text-gray-800">
                    {formatNumber(totalValue)} บาท
                  </Typography>
                </div>
              </CardContent>
            </Card>
            )}
            {!hasPermission(["inventory_view_changes"]) && (
               <Card className="rounded-2xl shadow-sm border border-gray-100 h-full">
               <CardContent className="flex items-center p-4">
                 <Box className="p-3 rounded-xl bg-purple-50 text-purple-600 mr-4">
                   <CategoryIcon fontSize="medium" />
                 </Box>
                 <div>
                   <Typography variant="body2" className="text-gray-500 mb-1">
                     ประเภทสินค้า
                   </Typography>
                   <Typography variant="h5" fontWeight="bold" className="text-gray-800">
                     {/* You might want to calculate this distinct categories count if needed, placeholder for now */}
                     {new Set(rows.map(r => r.category)).size} หมวด
                   </Typography>
                 </div>
               </CardContent>
             </Card>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* DataGrid Container */}
      <Box className="flex-grow px-4 md:px-6 pb-6 overflow-hidden flex flex-col">
        <Paper className="flex-grow rounded-2xl shadow-sm border border-gray-200 overflow-hidden bg-white">
          <StripedDataGrid
            rows={filteredRows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            rowHeight={60}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            localeText={{
              noRowsLabel: "ไม่พบข้อมูล",
            }}
            sx={{
              "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                outline: "none",
              },
            }}
          />
        </Paper>
      </Box>
    </div>
  );
}
